import React from 'react';
import { Document, Page, Svg, Path, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';
import { PropertyReport } from "../../../Models/PropertyReport";

// Safe Image component that handles errors gracefully
const SafeImage: React.FC<{ src?: string; style?: any; key?: any }> = ({ src, style }) => {
    if (!src) return null;
    
    // For base64 images, use directly - this is the most reliable
    if (src.startsWith('data:')) {
        // Validate base64 format and ensure it's a valid data URL
        try {
            // Check if it's a valid data URL format
            const isValidDataUrl = /^data:image\/(jpeg|jpg|png|gif|webp|svg\+xml);base64,/.test(src);
            if (!isValidDataUrl) {
                console.warn('Invalid data URL format, attempting to render anyway:', src.substring(0, 50));
            }
            return <Image src={src} style={style} />;
        } catch (error) {
            console.error('Error rendering base64 image:', error);
            return null;
        }
    }
    
    // For URLs, use object format with cache disabled
    try {
        return (
            <Image 
                src={{ 
                    uri: src, 
                    method: 'GET', 
                    headers: { 'Cache-Control': 'no-cache' }, 
                    body: '' 
                }}
                style={style}
            />
        );
    } catch (error) {
        console.error('Error rendering image:', src, error);
        return null;
    }
};

// import { Font } from '@react-pdf/renderer';
Font.register({
    family: 'Amiri',
    fonts: [
        { src: "/assets/fonts/Amiri-Regular.ttf", fontWeight: 'normal' },
        { src: "/assets/fonts/Amiri-Bold.ttf", fontWeight: 'bold' }
    ]
});

// Define styles for PDF
const styles = StyleSheet.create({
    pageWithPadding: {
        backgroundColor: '#FFFFFF',
        padding: 20,
        fontFamily: 'Amiri',
    },
    imagesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
    },
    gridImage: {
        width: '32%',
        height: 300,
        borderRadius: 12,
        objectFit: 'cover',
    },
    sectionTitle: {
        fontSize: 26,
        fontWeight: 700,
        color: '#212529',
        marginBottom: 20,
    },
    detailsRow: {
        flexDirection: 'row',
        gap: 20,
        marginBottom: 20,
    },
    detailsImage: {
        width: '60%',
        height: 300,
        borderRadius: 12,
        objectFit: 'cover',
    },
    detailsStats: {
        width: '40%',
        gap: 15,
    },
    statCard: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: '12px',
        backgroundColor: '#f8f9fa',
        padding: '12px',
        borderRadius: '12px',
        border: '1px solid #dee2e6',
        boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
    },
    statValue: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: 8
    },
    statLabel: {
        fontSize: '14px',
        color: '#6c757d'
    },
    description: {
        backgroundColor: '#f8f9fa',
        padding: 15,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        marginBottom: 20,
    },
    descriptionText: {
        fontSize: 16,
        color: '#6c757d',
        lineHeight: 1.7,
    },
    floorPlanImage: {
        width: '100%',
        height: 450,
        borderRadius: 12,
        objectFit: 'contain',
        marginBottom: 20,
    },
    mapPlaceholder: {
        width: '100%',
        height: 400,
        backgroundColor: '#F1F5F9',
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 20,
    },
    locationDescription: {
        backgroundColor: '#f8f9fa',
        padding: 15,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16
    },
    locationDescText: {
        fontSize: 16,
        color: '#6c757d',
        lineHeight: 1.7,
    },
    agentCard: {
        flexDirection: 'row',
        gap: 20,
        backgroundColor: '#FFFFFF',
        padding: 20,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        marginBottom: 20,
    },
    agentImage: {
        width: '100%',
        height: '100%',
        borderRadius: 12,
        objectFit: 'contain',
    },
    agentDetails: {
        flex: 1,
        gap: 10
    },
    agentName: {
        fontSize: 24,
        fontWeight: 700,
        color: '#212529',
        marginBottom: 20,
    },
    agentMessage: {
        fontSize: 16,
        color: '#6c757d',
        lineHeight: 1.7,
        marginTop: 15,
    },
    footer: {
        backgroundColor: '#746AFF',
        padding: 20,
        borderRadius: 12,
        marginTop: 24,
        alignItems: 'center',
    },
    footerRow: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 10,
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    footerText: {
        fontSize: 16,
        color: '#FFFFFF',
    },
    footerCompany: {
        fontSize: 20,
        color: '#FFFFFF',
        fontWeight: 700,
    },
});

interface DocumentPdfProps {
    propertyReport: PropertyReport;
}

const DocumentPdf: React.FC<DocumentPdfProps> = ({ propertyReport }) => {

    const capitalizeFirstLetter = (word: string) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    const capitalizeWordsInSentence = (offeringType: string) => {
        const offeringTypeValue = offeringType.replace('-', ' ');
        const words = offeringTypeValue.split(' ');
        const capitalizedWords = words.map(capitalizeFirstLetter);
        return capitalizedWords.join(' ');
    }

    const getMapboxImage = (lat: any, lng: any) => {
        const accessToken = "sk.eyJ1Ijoia2FvdXRhcjM0NTYiLCJhIjoiY203aXl1OW1iMDFvNTJrc2N5MjVxaTM5dyJ9.liGIzZ8bLftpvi635AiVYw";
        return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l-marker+f74e4e(${lng},${lat})/${lng},${lat},13,0,0/800x450?attribution=false&logo=false&access_token=${accessToken}`;
    };

    const mapImage = getMapboxImage(
        propertyReport.heroSection?.address?.latitude,
        propertyReport.heroSection?.address?.longitude
    );

    return (

        <Document>

            {/* Page 1: Hero Section - Full Screen */}
            <Page size={{ width: 900, height: 800 }}>
                <View style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    backgroundColor: "white",
                    padding: 20,
                    borderRadius: 12
                }}>
                    {propertyReport?.heroSection?.coverImageUrl && (
                        <SafeImage
                            src={propertyReport.heroSection.coverImageUrl}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                            }}
                        />
                    )}

                    {/* Dark gradient overlay */}
                    <View style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.3)"
                    }} />

                    {/* Logo and Title - Top Right */}
                    <View style={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        maxWidth: "40%"
                    }}>
                        <View style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            padding: 12,
                            borderRadius: 8
                        }}>
                        {propertyReport.company?.logo && (
                            <View style={{ backgroundColor: '#FFFFFF', padding: 4, borderRadius: 4, marginBottom: 8 }}>
                                <SafeImage
                                    src={propertyReport.company.logo}
                                    style={{ height: 32, width: 100, objectFit: 'contain' }}
                                />
                            </View>
                        )}
                            <Text style={{
                                fontSize: 16,
                                fontWeight: 500,
                                lineHeight: 1.4,
                                marginBottom: 0,
                                color: "#000000"
                            }}>
                                {propertyReport.heroSection?.title}
                            </Text>
                        </View>
                    </View>

                    {/* Location and Price - Bottom Left */}
                    <View style={{
                        position: "absolute",
                        bottom: 30,
                        left: 30,
                        right: 30
                    }}>
                        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8, gap: 6 }}>
                            <Svg width="24" height="24" viewBox="0 0 24 24">
                                <Path
                                    d="M12 2a7.008 7.008 0 0 0-7 7c0 5.353 6.036 11.45 6.293 11.707l.707.707.707-.707C12.964 20.45 19 14.353 19 9a7.008 7.008 0 0 0-7-7zm0 16.533C10.471 16.825 7 12.553 7 9a5 5 0 0 1 10 0c0 3.546-3.473 7.823-5 9.533z"
                                    fill="#FFFFFF"
                                />
                                <Path
                                    d="M12 6a3 3 0 1 0 3 3 3 3 0 0 0-3-3zm0 4a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"
                                    fill="#FFFFFF"
                                />
                            </Svg>
                            <Text style={{
                                fontSize: 22,
                                color: "#FFFFFF",
                                fontWeight: 700
                            }}>
                                {propertyReport?.heroSection?.location}
                            </Text>
                        </View>
                        <Text style={{ fontSize: 28, color: "#FFFFFF", margin: 0, fontWeight: 700 }}>
                            $ {propertyReport?.propertyDetails?.price?.amount?.toLocaleString('en-US') || 'N/A'}
                        </Text>
                    </View>
                </View>
            </Page>

            {/* Page 2: Property Gallery */}
            <Page size={{ width: 900, height: 800 }} style={{
                padding: 20,
                borderBottom: "2px solid #dee2e6",
                minHeight: 800,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start"
            }}>
                <View style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24
                }}>
                    {propertyReport.company?.logo && (
                        <View style={{ backgroundColor: '#FFFFFF', padding: 4, borderRadius: 4 }}>
                            <SafeImage src={propertyReport.company.logo} style={{ height: 40, objectFit: 'contain' }} />
                        </View>
                    )}
                    <View style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                        <View style={{
                            backgroundColor: "#f8fafc",
                            paddingTop: 10,
                            paddingBottom: 10,
                            paddingLeft: 17,
                            paddingRight: 17,
                            borderRadius: 20,
                            border: "2px solid #e2e8f0",
                            minWidth: 122,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <Text style={{
                                fontSize: 14,
                                color: "#64748b",
                                fontWeight: 700,
                                lineHeight: 1.28,
                                textAlign: "center",
                            }}>
                                {propertyReport?.propertyGallery?.propertyType}
                            </Text>
                        </View>
                        <View style={{
                            backgroundColor: "#746AFF",
                            paddingTop: 10,
                            paddingBottom: 10,
                            paddingLeft: 17,
                            paddingRight: 17,
                            borderRadius: 20,
                            border: `2px solid #746AFF`,
                            minWidth: 122,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                        }}>
                            <Text style={{
                                fontSize: 14,
                                color: "#ffffff",
                                fontWeight: 700,
                                lineHeight: 1.28,
                                textAlign: "center",

                            }}>
                                {propertyReport?.propertyGallery?.offeringType && capitalizeWordsInSentence(propertyReport.propertyGallery.offeringType)}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={{ flexDirection: "row", gap: 16, marginBottom: 16 }}>
                    {propertyReport?.heroSection?.heroImageUrl && (
                        <View style={{ flex: 2 }}>
                            <SafeImage
                                src={propertyReport.heroSection.heroImageUrl}
                                style={{ width: "100%", height: 400, borderRadius: 12, objectFit: "cover" }}
                            />
                        </View>
                    )}
                    <View style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
                        {(propertyReport?.propertyGallery?.images || []).filter(img => img).slice(0, 3).map((img, i) => (
                            <SafeImage
                                key={i}
                                src={img}
                                style={{ width: "100%", height: 125, borderRadius: 12, objectFit: "cover" }}
                            />
                        ))}
                    </View>
                </View>
                <View style={{ display: "flex", flexDirection: "row", gap: 16 }}>
                    {(propertyReport?.propertyGallery?.images || []).filter(img => img).slice(3, 6).map((img, i) => (
                        <SafeImage
                            key={i}
                            src={img}
                            style={{ width: "32%", height: 125, borderRadius: 12, objectFit: "cover" }}
                        />
                    ))}
                </View>
            </Page>

            {/* Page 3: Additional Images (if available) */}
            {propertyReport.listImages && propertyReport.listImages.length > 0 && (
                <Page size={{ width: 900, height: 800 }} style={styles.pageWithPadding}>
                    <View style={styles.imagesGrid}>
                        {propertyReport.listImages.filter((image: any) => image?.src).slice(0, 9).map((image: any, index: number) => (
                            <SafeImage
                                key={index}
                                src={image.src}
                                style={styles.gridImage}
                            />
                        ))}
                    </View>
                </Page>
            )}

            {/* Page 4: About the Property */}
            <Page size={{ width: 900, height: 800 }} style={styles.pageWithPadding}>
                <Text style={styles.sectionTitle}>About This Property</Text>

                <View style={styles.detailsRow}>
                    {propertyReport?.propertyDetails?.image && (
                        <SafeImage
                            src={propertyReport.propertyDetails.image}
                            style={styles.detailsImage}
                        />
                    )}
                    <View style={styles.detailsStats}>

                        <View style={styles.statCard}>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                <View style={{ padding: 12, borderRadius: 12, backgroundColor: '#EDE9FE', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Svg width="20" height="20" viewBox="0 0 20 20">
                                        <Path
                                            d="M16.875 9.01172C16.481 8.83876 16.0553 8.74963 15.625 8.75H4.375C3.94473 8.74959 3.51907 8.83858 3.125 9.01133C2.56805 9.25489 2.09412 9.65543 1.76113 10.164C1.42815 10.6726 1.25053 11.2671 1.25 11.875V16.25C1.25 16.4158 1.31585 16.5747 1.43306 16.6919C1.55027 16.8092 1.70924 16.875 1.875 16.875C2.04076 16.875 2.19973 16.8092 2.31694 16.6919C2.43415 16.5747 2.5 16.4158 2.5 16.25V15.9375C2.50101 15.8549 2.53426 15.776 2.59265 15.7177C2.65104 15.6593 2.72993 15.626 2.8125 15.625H17.1875C17.2701 15.626 17.349 15.6593 17.4074 15.7177C17.4657 15.776 17.499 15.8549 17.5 15.9375V16.25C17.5 16.4158 17.5658 16.5747 17.6831 16.6919C17.8003 16.8092 17.9592 16.875 18.125 16.875C18.2908 16.875 18.4497 16.8092 18.5669 16.6919C18.6842 16.5747 18.75 16.4158 18.75 16.25V11.875C18.7494 11.2672 18.5717 10.6727 18.2388 10.1642C17.9058 9.65574 17.4319 9.25525 16.875 9.01172Z"
                                            fill="#746AFF"
                                        />
                                        <Path
                                            d="M14.6875 3.125H5.3125C4.73234 3.125 4.17594 3.35547 3.7657 3.7657C3.35547 4.17594 3.125 4.73234 3.125 5.3125V8.125C3.12502 8.14921 3.13066 8.17309 3.14149 8.19475C3.15231 8.21641 3.16803 8.23525 3.18738 8.2498C3.20674 8.26434 3.22921 8.27419 3.25303 8.27857C3.27684 8.28294 3.30135 8.28172 3.32461 8.275C3.6658 8.17521 4.01951 8.1247 4.375 8.125H4.54023C4.5788 8.12524 4.6161 8.11122 4.64494 8.08562C4.67379 8.06002 4.69215 8.02465 4.69648 7.98633C4.73057 7.68093 4.87596 7.3988 5.10488 7.1938C5.3338 6.9888 5.63021 6.87531 5.9375 6.875H8.125C8.43249 6.87502 8.72919 6.98839 8.95835 7.19341C9.18752 7.39843 9.33308 7.68073 9.36719 7.98633C9.37153 8.02465 9.38988 8.06002 9.41873 8.08562C9.44758 8.11122 9.48487 8.12524 9.52344 8.125H10.4789C10.5175 8.12524 10.5548 8.11122 10.5836 8.08562C10.6125 8.06002 10.6308 8.02465 10.6352 7.98633C10.6692 7.68113 10.8144 7.39916 11.0431 7.19419C11.2718 6.98922 11.5679 6.8756 11.875 6.875H14.0625C14.37 6.87502 14.6667 6.98839 14.8959 7.19341C15.125 7.39843 15.2706 7.68073 15.3047 7.98633C15.309 8.02465 15.3274 8.06002 15.3562 8.08562C15.3851 8.11122 15.4224 8.12524 15.4609 8.125H15.625C15.9805 8.12483 16.3342 8.17547 16.6754 8.27539C16.6987 8.28212 16.7232 8.28333 16.7471 8.27894C16.7709 8.27455 16.7934 8.26466 16.8128 8.25007C16.8321 8.23548 16.8478 8.21659 16.8586 8.19488C16.8694 8.17317 16.875 8.14925 16.875 8.125V5.3125C16.875 4.73234 16.6445 4.17594 16.2343 3.7657C15.8241 3.35547 15.2677 3.125 14.6875 3.125Z"
                                            fill="#746AFF"
                                        />
                                    </Svg>
                                </View>
                                <View>
                                    <Text style={styles.statValue}>
                                        {propertyReport?.propertyDetails?.bedrooms}
                                    </Text>
                                    <Text style={styles.statLabel}>Bedrooms</Text>
                                </View>
                            </View>
                        </View>

                        <View style={styles.statCard}>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                <View style={{ padding: 12, borderRadius: 12, backgroundColor: '#EDE9FE', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Svg width="20" height="20" viewBox="0 0 20 20">
                                        <Path d="M15.2734 9.57031C15.2734 8.92414 14.7477 8.39844 14.1016 8.39844H12.9297C12.2835 8.39844 11.7578 8.92414 11.7578 9.57031V13.6719H15.2734V9.57031Z" fill="#746AFF" />
                                        <Path d="M2.38281 2.92969C2.38281 1.96055 3.17148 1.17188 4.14062 1.17188H5.3125C6.10445 1.17188 6.77562 1.69859 6.99469 2.42C5.69695 2.72086 4.72656 3.88555 4.72656 5.27344V5.85938H10.5859V5.27344C10.5859 3.84109 9.55246 2.64637 8.19219 2.39375C7.93957 1.03336 6.74469 0 5.3125 0H4.14062C2.52539 0 1.21094 1.31406 1.21094 2.92969V8.39844H2.38281V2.92969Z" fill="#746AFF" />
                                        <Path d="M16.4453 14.8438H10.5859V13.0859H1.21094V14.8438C1.21094 16.377 2.05664 17.7161 3.30594 18.4198L2.55445 19.1713L3.38305 20L4.51566 18.8674C4.77352 18.9183 5.03992 18.9453 5.3125 18.9453H14.6875C14.9601 18.9453 15.2265 18.9183 15.4843 18.8674L16.617 20L17.4456 19.1714L16.6941 18.4199C17.9434 17.7161 18.7891 16.377 18.7891 14.8438V13.0859H16.4453V14.8438Z" fill="#746AFF" />
                                        <Path d="M18.8281 9.57031H16.4453V11.9141H18.8281C19.4742 11.9141 20 11.3883 20 10.7422C20 10.0961 19.4742 9.57031 18.8281 9.57031Z" fill="#746AFF" />
                                        <Path d="M10.5859 9.57031H1.17188C0.525781 9.57031 0 10.0961 0 10.7422C0 11.3883 0.525781 11.9141 1.17188 11.9141H10.5859V9.57031Z" fill="#746AFF" />
                                    </Svg>
                                </View>
                                <View>
                                    <Text style={styles.statValue}>
                                        {propertyReport?.propertyDetails?.bathrooms}
                                    </Text>
                                    <Text style={styles.statLabel}>Bathrooms</Text>
                                </View>
                            </View>
                        </View>

                        <View style={styles.statCard}>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                <View style={{ padding: 12, borderRadius: 12, backgroundColor: '#EDE9FE', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Svg width="20" height="20" viewBox="0 0 20 20">
                                        <Path d="M13.491 1.6665H6.50768C3.47435 1.6665 1.66602 3.47484 1.66602 6.50817V13.4832C1.66602 16.5248 3.47435 18.3332 6.50768 18.3332H13.4827C16.516 18.3332 18.3243 16.5248 18.3243 13.4915V6.50817C18.3327 3.47484 16.5243 1.6665 13.491 1.6665ZM15.6243 8.33317C15.6243 8.67484 15.341 8.95817 14.9993 8.95817C14.6577 8.95817 14.3743 8.67484 14.3743 8.33317V6.50817L6.50768 14.3748H8.33268C8.67435 14.3748 8.95768 14.6582 8.95768 14.9998C8.95768 15.3415 8.67435 15.6248 8.33268 15.6248H4.99935C4.91602 15.6248 4.83268 15.6082 4.75768 15.5748C4.60768 15.5082 4.48268 15.3915 4.41602 15.2332C4.39102 15.1582 4.37435 15.0832 4.37435 14.9998V11.6665C4.37435 11.3248 4.65768 11.0415 4.99935 11.0415C5.34102 11.0415 5.62435 11.3248 5.62435 11.6665V13.4915L13.491 5.62484H11.666C11.3243 5.62484 11.041 5.3415 11.041 4.99984C11.041 4.65817 11.3243 4.37484 11.666 4.37484H14.9993C15.0827 4.37484 15.1577 4.3915 15.241 4.42484C15.391 4.4915 15.516 4.60817 15.5827 4.7665C15.6077 4.8415 15.6243 4.9165 15.6243 4.99984V8.33317Z" fill="#746AFF" />
                                    </Svg>
                                </View>
                                <View>
                                    <Text style={styles.statValue}>
                                        {propertyReport?.propertyDetails?.size?.value}
                                    </Text>
                                    <Text style={styles.statLabel}>
                                        {propertyReport?.propertyDetails?.size?.unit}
                                    </Text>
                                </View>
                            </View>
                        </View>

                    </View>
                </View>

                <View style={styles.description}>
                    <Text style={styles.descriptionText}>
                        {propertyReport?.propertyDetails?.description}
                    </Text>
                </View>
            </Page>

            {/* Page 5: Floor Plan (if available) */}
            {propertyReport?.floorPlan?.imageUrl && (
                <Page size={{ width: 900, height: 800 }} style={styles.pageWithPadding}>
                    <Text style={styles.sectionTitle}>Floor Plan</Text>

                    <SafeImage
                        src={propertyReport.floorPlan.imageUrl}
                        style={styles.floorPlanImage}
                    />

                    {propertyReport?.floorPlan?.description && (
                        <View style={styles.description}>
                            <Text style={styles.descriptionText}>
                                {propertyReport.floorPlan.description}
                            </Text>
                        </View>
                    )}
                </Page>
            )}

            {/* Page 6: Location */}
            <Page size={{ width: 900, height: 800 }} style={styles.pageWithPadding}>
                <Text style={styles.sectionTitle}>Location</Text>

                <View style={styles.mapPlaceholder}>
                    <SafeImage
                        src={propertyReport?.location?.mapImageUrl || mapImage}
                        style={{ height: '100%', width: '100%', borderRadius: 12 }}
                    />
                </View>

                {propertyReport?.location?.description && (
                    <View style={styles.locationDescription}>
                        <View style={{ width: 24, height: 24, flexShrink: 0 }}>
                            <Svg width="24" height="24" viewBox="0 0 24 24">
                                <Path
                                    d="M12 2a7.008 7.008 0 0 0-7 7c0 5.353 6.036 11.45 6.293 11.707l.707.707.707-.707C12.964 20.45 19 14.353 19 9a7.008 7.008 0 0 0-7-7zm0 16.533C10.471 16.825 7 12.553 7 9a5 5 0 0 1 10 0c0 3.546-3.473 7.823-5 9.533z"
                                    fill="#746AFF"
                                />
                                <Path
                                    d="M12 6a3 3 0 1 0 3 3 3 3 0 0 0-3-3zm0 4a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"
                                    fill="#746AFF"
                                />
                            </Svg>
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.locationDescText}>
                                {propertyReport.location.description}
                            </Text>
                        </View>
                    </View>
                )}
            </Page>

            {/* Page 7: Agent Information */}
            <Page size={{ width: 900, height: 800 }} style={styles.pageWithPadding}>
                <Text style={styles.sectionTitle}>Contact Agent</Text>

                <View style={styles.agentCard}>
                    <View style={{ position: 'relative', height: "280px", width: "250px" }}>
                        {propertyReport?.agentInfo?.photoUrl && (
                            <SafeImage
                                src={propertyReport.agentInfo.photoUrl}
                                style={styles.agentImage}
                            />
                        )}
                        <View style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: 12,
                            zIndex: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.1)"
                        }} />

                    </View>

                    <View style={styles.agentDetails}>
                        <Text style={styles.agentName}>
                            {propertyReport.agentInfo?.name
                                ? propertyReport.agentInfo.name.charAt(0).toUpperCase() +
                                propertyReport.agentInfo.name.slice(1).toLowerCase()
                                : ''}
                        </Text>
                        <View style={styles.statCard}>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                <View style={{ padding: 12, borderRadius: 12, backgroundColor: '#EDE9FE', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Svg width="20" height="20" viewBox="0 0 24 24">
                                        <Path d="M21.384,17.752a2.108,2.108,0,0,1-.522,3.359,7.543,7.543,0,0,1-5.476.642C10.5,20.523,3.477,13.5,2.247,8.614a7.543,7.543,0,0,1,.642-5.476,2.108,2.108,0,0,1,3.359-.522L8.333,4.7a2.094,2.094,0,0,1,.445,2.328A3.877,3.877,0,0,1,8,8.2c-2.384,2.384,5.417,10.185,7.8,7.8a3.877,3.877,0,0,1,1.173-.781,2.092,2.092,0,0,1,2.328.445Z" fill="#746AFF" />
                                    </Svg>
                                </View>
                                <View>
                                    <Text style={styles.statValue}>
                                        Phone
                                    </Text>
                                    <Text style={styles.statLabel}>{propertyReport.agentInfo?.phone}</Text>
                                </View>
                            </View>
                        </View>

                        <View style={styles.statCard}>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                <View style={{ padding: 12, borderRadius: 12, backgroundColor: '#EDE9FE', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Svg width="20" height="20" viewBox="0 0 24 24">
                                        <Path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#746AFF" />
                                    </Svg>
                                </View>
                                <View>
                                    <Text style={styles.statValue}>
                                        Email
                                    </Text>
                                    <Text style={styles.statLabel}>
                                        {propertyReport.agentInfo?.email}</Text>
                                </View>
                            </View>
                        </View>

                        <Text style={styles.agentMessage}>
                            I'm here to help you find your dream home. Contact me today to schedule a viewing
                            or to learn more about this exceptional property.
                        </Text>
                    </View>
                </View>

                <View style={styles.footer}>
                    <View style={styles.footerRow}>
                        {propertyReport.company?.phone && (
                            <Text style={styles.footerText}>
                                {propertyReport.company.phone}
                            </Text>
                        )}
                        {propertyReport.company?.phone && propertyReport.company?.email && (
                            <Text style={styles.footerText}>•</Text>
                        )}
                        {propertyReport.company?.email && (
                            <Text style={styles.footerText}>
                                {propertyReport.company.email}
                            </Text>
                        )}
                    </View>
                    <Text style={styles.footerCompany}>
                        {propertyReport?.company?.name}
                    </Text>
                </View>

            </Page>

        </Document>
    );
};

export default DocumentPdf;